import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Mail, Pencil, Plus, Shield, UserRound, X } from "lucide-react";
import {
  activateUser,
  deactivateUser,
  deleteUser,
  getClient,
  listClientUsers,
  restoreUser,
} from "../../../api/chatbotApi";

const getUserList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.users)) return response.users;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getRoleLabel = (role) => {
  if (Number(role) === 0) return "Super Admin";
  if (Number(role) === 1) return "Account Admin";
  return role || "User";
};

export default function ChatbotClientUsersPage() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const loadUsers = async () => {
    const response = await listClientUsers(clientId);
    setUsers(getUserList(response));
  };

  const runUserAction = async (user, action) => {
    const userId = user.id || user.user_id;
    if (!userId) {
      setError("This user does not have a valid ID.");
      return;
    }
    setActionLoading(`${action}-${userId}`);
    setError("");
    try {
      const actions = { activate: activateUser, deactivate: deactivateUser, delete: deleteUser, restore: restoreUser };
      await actions[action](userId);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || `Failed to ${action} user`);
    } finally {
      setActionLoading("");
    }
  };

  const requestConfirmation = (user, action) => {
    setPendingAction({ user, action });
  };

  const confirmPendingAction = async () => {
    if (!pendingAction) return;
    const { user, action } = pendingAction;
    setPendingAction(null);
    await runUserAction(user, action);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
          const [clientResponse, usersResponse] = await Promise.all([
          getClient(clientId),
          listClientUsers(clientId),
        ]);
        if (!active) return;
        setClient(clientResponse);
        setUsers(getUserList(usersResponse));
      } catch (err) {
        if (active) setError(err.response?.data?.detail || err.message || "Failed to load client users");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [clientId]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/clients" className="inline-flex items-center gap-1 transition hover:text-slate-900">
          <ArrowLeft size={16} /> Clients
        </Link>
        <span>/</span>
        <Link to={`/clients/${clientId}`} className="transition hover:text-slate-900">
          {client?.name || "Client"}
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">Users</span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <UserRound size={24} className="text-[#096dea]" /> Users
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Users associated with {client?.name || "this client"}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!loading && <span className="text-sm font-medium text-slate-500">{users.length} users</span>}
          <Link
            to={`/clients/${clientId}/users/new-user`}
            className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0959bb]"
          >
            <Plus size={17} /> Add User
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <UserRound className="mx-auto h-12 w-12 text-slate-300" />
            <h2 className="mt-3 text-base font-semibold text-slate-900">No users found</h2>
            <p className="mt-1 text-sm text-slate-500">There are no users associated with this client.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">User</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Role</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {users.map((user, index) => {
                  const name = user.name || user.full_name || user.username || "Unnamed user";
                  const email = user.email || user.email_address || "No email";
                  const userId = user.id || user.user_id;
                  const isInactive = user.is_active === false || user.status === "inactive";
                  const isDeleted = user.deleted === true || user.deleted_at || user.is_deleted === true || user.status === "deleted";
                  return (
                    <tr key={user.id || user.user_id || email || index} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-slate-900">{name}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-2"><Mail size={14} /> {email}</span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-2"><Shield size={14} /> {getRoleLabel(user.role ?? user.user_role)}</span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${user.is_active === false || user.status === "inactive" ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700"}`}>
                          {isDeleted ? "Deleted" : isInactive ? "Inactive" : user.status || "Active"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/clients/${clientId}/users/${userId}/edit`}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]"
                          >
                            <Pencil size={13} /> Edit
                          </Link>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isInactive}
                            aria-label={`${isInactive ? "Activate" : "Deactivate"} ${name}`}
                            disabled={actionLoading === `${isInactive ? "activate" : "deactivate"}-${userId}`}
                            onClick={() => (isInactive ? runUserAction(user, "activate") : requestConfirmation(user, "deactivate"))}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-semibold transition disabled:opacity-50 ${
                              isInactive
                                ? "border-slate-200 bg-slate-100 text-slate-600"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            <span className={`relative h-4 w-7 rounded-full transition ${isInactive ? "bg-slate-300" : "bg-emerald-500"}`}>
                              <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition ${isInactive ? "left-0.5" : "left-3.5"}`} />
                            </span>
                            {isInactive ? "Activate" : "Active"}
                          </button>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={Boolean(isDeleted)}
                            aria-label={`${isDeleted ? "Restore" : "Delete"} ${name}`}
                            disabled={actionLoading === `${isDeleted ? "restore" : "delete"}-${userId}`}
                            onClick={() => (isDeleted ? runUserAction(user, "restore") : requestConfirmation(user, "delete"))}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-semibold transition disabled:opacity-50 ${
                              isDeleted
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                            }`}
                          >
                            <span className={`relative h-4 w-7 rounded-full transition ${isDeleted ? "bg-emerald-500" : "bg-rose-300"}`}>
                              <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition ${isDeleted ? "left-3.5" : "left-0.5"}`} />
                            </span>
                            {isDeleted ? "Deleted" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {pendingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  {pendingAction.action === "delete" ? "Delete user?" : "Deactivate user?"}
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  {pendingAction.action === "delete"
                    ? `Delete ${pendingAction.user.name || pendingAction.user.email || "this user"}? You can restore this user later.`
                    : `Deactivate ${pendingAction.user.name || pendingAction.user.email || "this user"}? They will lose access until activated again.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPendingAction(null)}
                className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close confirmation"
              >
                <X size={18} />
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingAction(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmPendingAction}
                className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                {pendingAction.action === "delete" ? "Delete User" : "Deactivate User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
